'use client';

import React from 'react';
import Link from 'next/link';
import {
  EnvironmentOutlined,
  CompassOutlined,
  CarOutlined,
} from '@ant-design/icons';

const popularDestinations = [
  { label: 'New South Wales', href: '/apps/campsite-explorer/regions/states/new-south-wales' },
  { label: 'Victoria', href: '/apps/campsite-explorer/regions/states/victoria' },
  { label: 'Queensland', href: '/apps/campsite-explorer/regions/states/queensland' },
  { label: 'Western Australia', href: '/apps/campsite-explorer/regions/states/western-australia' },
  { label: 'South Australia', href: '/apps/campsite-explorer/regions/states/south-australia' },
  { label: 'Tasmania', href: '/apps/campsite-explorer/regions/states/tasmania' },
];

const popularRoutes = [
  { label: 'Sydney to Byron Bay', start: 'Sydney', end: 'Byron Bay' },
  { label: 'Melbourne to Adelaide', start: 'Melbourne', end: 'Adelaide' },
  { label: 'Cairns to Cape Tribulation', start: 'Cairns', end: 'Cape Tribulation' },
  { label: 'Perth to Esperance', start: 'Perth', end: 'Esperance' },
  { label: 'Adelaide to Alice Springs', start: 'Adelaide', end: 'Alice Springs' },
  { label: 'Hobart to Cradle Mountain', start: 'Hobart', end: 'Cradle Mountain' },
];

const Footer: React.FC = () => {
  return (
    <footer className="bg-slate-900 text-white mt-auto">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <h3 className="text-lg font-bold mb-3">Camping Australia</h3>
            <p className="text-sm text-slate-400 leading-relaxed mb-4">
              Discover amazing camping destinations across Australia. Find free camping, caravan parks, and beautiful campsites for your next adventure.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link
                href="/apps/campsite-explorer/owner-portal"
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 px-4 py-2 text-xs font-semibold text-white transition hover:brightness-110 hover:shadow-[0_0_16px_rgba(56,189,248,0.25)]"
              >
                Manage a Campsite
              </Link>
              <Link
                href="/apps/campsite-explorer/recommend-campsite"
                className="inline-flex items-center justify-center rounded-full border border-slate-600 px-4 py-2 text-xs font-semibold text-slate-300 transition hover:border-slate-400 hover:text-white"
              >
                Recommend a Site
              </Link>
            </div>
          </div>

          {/* Browse by Region */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
              <EnvironmentOutlined style={{ fontSize: '14px' }} />
              Browse by Region
            </h4>
            <ul className="space-y-2">
              {popularDestinations.map((item) => (
                <li key={item.label}>
                  <Link
                    href={item.href}
                    className="text-sm text-slate-400 transition hover:text-white"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Routes */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
              <CarOutlined style={{ fontSize: '14px' }} />
              Popular Routes
            </h4>
            <ul className="space-y-2">
              {popularRoutes.map((item) => (
                <li key={item.label}>
                  <Link
                    href={`/apps/campsite-explorer?isRouteSearch=true&startPoint=${encodeURIComponent(item.start)}&endPoint=${encodeURIComponent(item.end)}`}
                    className="text-sm text-slate-400 transition hover:text-white"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2">
              <CompassOutlined style={{ fontSize: '14px' }} />
              Explore
            </h4>
            <ul className="space-y-2">
              <li>
                <Link href="/apps/campsite-explorer/regions" className="text-sm text-slate-400 transition hover:text-white">
                  All Regions
                </Link>
              </li>
              <li>
                <Link href="/apps/campsite-explorer/saved" className="text-sm text-slate-400 transition hover:text-white">
                  Saved Campsites
                </Link>
              </li>
              <li>
                <Link href="https://campingaustralia.com.au/pages/about-outdoor-camping-australia" className="text-sm text-slate-400 transition hover:text-white">
                  About Us
                </Link>
              </li>
              <li>
                <Link href="https://campingaustralia.com.au" className="text-sm text-slate-400 transition hover:text-white">
                  Shop
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-800">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} Camping Australia. All rights reserved.
          </p>
          <nav className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <Link href="https://campingaustralia.com.au/pages/about-outdoor-camping-australia" className="text-slate-500 transition hover:text-slate-300">
              About
            </Link>
            <Link href="https://campingaustralia.com.au/pages/contact-us" className="text-slate-500 transition hover:text-slate-300">
              Contact
            </Link>
            <Link href="https://campingaustralia.com.au/policies/privacy-policy" className="text-slate-500 transition hover:text-slate-300">
              Privacy
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
